import React from "react";
import ReactDOM from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import "./AnimatedModal.css";

interface AnimatedModalProps {
  /** Controls visibility */
  isOpen: boolean;
  /** Called when backdrop or close button is clicked */
  onClose: () => void;
  /** Modal content */
  children: React.ReactNode;
  /** Max width of the modal shell (default: 560px) */
  maxWidth?: number | string;
  /** z-index for the backdrop (default: 1200) */
  backdropZIndex?: number;
  /** z-index for the modal container (default: 1201) */
  modalZIndex?: number;
  /** Accessible label for the dialog */
  ariaLabel?: string;
  /** Extra class(es) applied to the modal shell — use for size/style overrides */
  className?: string;
}

const AnimatedModal: React.FC<AnimatedModalProps> = ({
  isOpen,
  onClose,
  children,
  maxWidth = 560,
  backdropZIndex = 1200,
  modalZIndex = 1201,
  ariaLabel = "Dialog",
  className = "",
}) => {
  return ReactDOM.createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            key="am-backdrop"
            className="am-backdrop"
            style={{ zIndex: backdropZIndex }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
          />

          {/* Centering container */}
          <div className="am-container" style={{ zIndex: modalZIndex }}>
            <motion.div
              key="am-modal"
              className={`am-modal${className ? ` ${className}` : ""}`}
              role="dialog"
              aria-modal="true"
              aria-label={ariaLabel}
              style={{ maxWidth }}
              initial={{ opacity: 0, scale: 0.94, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.94, y: 16 }}
              transition={{ type: "spring", damping: 26, stiffness: 320 }}
            >
              {children}
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>,
    document.body,
  );
};

/** Convenience sub-components for a consistent header/body/footer layout */
const ModalHeader: React.FC<{
  title: string;
  subtitle?: string;
  onClose: () => void;
}> = ({ title, subtitle, onClose }) => (
  <div className="am-header">
    <div className="am-header__text">
      <h2 className="am-title">{title}</h2>
      {subtitle && <p className="am-subtitle">{subtitle}</p>}
    </div>
    <button className="am-close" onClick={onClose} aria-label="Close">
      <X size={18} />
    </button>
  </div>
);

const ModalBody: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="am-body">{children}</div>
);

const ModalFooter: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="am-footer">{children}</div>
);

export { AnimatedModal, ModalHeader, ModalBody, ModalFooter };
export default AnimatedModal;
